import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { BatchFullDialog, isBatchFull } from "@/components/attendance/BatchFullDialog";

export default function CandidateForm({ open, onClose, candidate, centers, batches, me, onSaved }) {
  const empty = { name: "", candidate_code: "", mobile: "", aadhaar_number: "", center_id: me?.center_id || centers[0]?.id || "", batch_id: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [full, setFull] = useState(null);
  useEffect(() => { if (open) setForm(candidate ? { ...empty, ...candidate, aadhaar_number: "", batch_id: candidate.batch_id || "" } : empty); }, [open, candidate]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, batch_id: form.batch_id || null, mobile: form.mobile || null, aadhaar_number: form.aadhaar_number || null };
      if (payload.aadhaar_number && !/^\d{12}$/.test(payload.aadhaar_number)) { toast.error("Aadhaar must be 12 digits"); setSaving(false); return; }
      if (candidate) await api.patch(`/attendance/candidates/${candidate.id}`, payload);
      else await api.post("/attendance/candidates", payload);
      toast.success(candidate ? "Candidate updated" : "Candidate added");
      onSaved(); onClose();
    } catch (e) { if (isBatchFull(e)) setFull(e.response.data.detail); else toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const centerBatches = batches.filter((b) => b.center_id === form.center_id);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <BatchFullDialog info={full} onClose={() => { setFull(null); setForm((f) => ({ ...f, batch_id: "" })); }} />
      <DialogContent data-testid="candidate-form" className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">{candidate ? "Edit Candidate" : "Add Candidate"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Candidate name *</Label><Input data-testid="candidate-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="space-y-1"><Label>Candidate ID *</Label><Input data-testid="candidate-code" value={form.candidate_code} onChange={(e) => setForm({ ...form, candidate_code: e.target.value })} /></div>
          <div className="space-y-1"><Label>Aadhaar Number {candidate?.aadhaar_last4 ? `(stored ••••${candidate.aadhaar_last4} — enter to replace)` : ""}</Label><Input data-testid="candidate-aadhaar" maxLength={12} value={form.aadhaar_number || ""} onChange={(e) => setForm({ ...form, aadhaar_number: e.target.value.replace(/\D/g, "") })} /></div>
          <div className="space-y-1"><Label>Mobile</Label><Input data-testid="candidate-mobile" value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
          {me?.role === "admin" && !candidate && (
            <div className="space-y-1"><Label>Center *</Label>
              <Select value={form.center_id} onValueChange={(v) => setForm({ ...form, center_id: v, batch_id: "" })}>
                <SelectTrigger data-testid="candidate-center"><SelectValue placeholder="Select center" /></SelectTrigger>
                <SelectContent>{centers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          {centerBatches.length > 0 && (
            <div className="space-y-1"><Label>Batch</Label>
              <Select value={form.batch_id} onValueChange={(v) => setForm({ ...form, batch_id: v })}>
                <SelectTrigger data-testid="candidate-batch"><SelectValue placeholder="Select batch" /></SelectTrigger>
                <SelectContent>{centerBatches.map((b) => <SelectItem key={b.id} value={b.id} disabled={b.is_full && b.id !== candidate?.batch_id}>{b.name}{b.capacity != null ? ` (${b.candidate_count}/${b.capacity}${b.is_full ? " — Full" : ""})` : ""}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button data-testid="candidate-save" onClick={save} disabled={saving || form.name.length < 2 || !form.candidate_code || !form.center_id} className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
